import {
  cardPrice,
  coverSrc,
  shopItemsUrl,
  shopPageQuery,
  withSectionHeaders,
  type CatalogKind,
  type Page,
  type PublicShop,
  type PublicShopItem,
} from "@fusiqo/vozqi-shared";
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import type { ProductsSnapshot } from "./shop-view";

/**
 * THE PRODUCTS PART OF THE PROFILE: the shop (spec 2026-09-26 §6), every item
 * it shares that fits `kind`.
 *
 * LAID OUT LIKE A REAL SHOP, the search on the left. On a wide
 * screen a sticky column on the left holds the search and the categories --
 * the business's sections holding this kind -- and the grid fills the rest;
 * choosing a category narrows the list on the server. On a phone the column
 * folds into the search and a category select above the grid (no chips).
 *
 * A card opens the item's own page (/p/<token>) in this tab; «atrás» comes
 * back to the same place: the profile saves a snapshot on tapping, with this
 * part's state from `latest`, and hands it back as `restored`.
 */

/** A pause after the last key before searching: the Worker's limit counts cache misses. */
const SEARCH_DELAY_MS = 300;

type ProductsProps = {
  shop: PublicShop;
  slug: string;
  /** PRODUCT beside a services list; null when this part is the whole shop. */
  kind: CatalogKind | null;
  /** The categories this part lists: only those holding its kind. */
  sections: PublicShop["sections"];
  /** Whether the page is still the business's «tienda» (no services beside it). */
  shopWord: boolean;
  /** Back from an item: this part as it was, or null to start afresh. */
  restored: ProductsSnapshot | null;
  /** This part as it stands, for the snapshot the profile saves. */
  latest: MutableRefObject<ProductsSnapshot | null>;
  /** Saves the snapshot: a card is opening. */
  onOpen: () => void;
};

export function ShopProducts({ shop, slug, kind, sections, shopWord, restored, latest, onOpen }: ProductsProps) {
  const [text, setText] = useState(restored?.q ?? "");
  const [q, setQ] = useState(restored?.q ?? "");
  const [section, setSection] = useState<string | null>(restored?.section ?? null);
  const [items, setItems] = useState<PublicShopItem[]>(restored?.items ?? []);
  // Which page listed each card (by token): its cover is vouched for by that page.
  const [pageOf, setPageOf] = useState<Record<string, string>>(restored?.pageOf ?? {});
  const [nextCursor, setNextCursor] = useState<string | null>(restored?.nextCursor ?? null);
  const [total, setTotal] = useState<number | null>(restored?.total ?? null);
  const [status, setStatus] = useState<"idle" | "loading" | "failed">(restored === null ? "loading" : "idle");
  /**
   * The filter the snapshot was taken under: while the list still shows it,
   * the restored cards stand -- and StrictMode's second effect run in
   * development sees the same, rather than a one-shot flag already spent.
   * Cleared the moment the filter changes.
   */
  const restoredFilter = useRef(restored === null ? null : `${restored.q}\u0000${restored.section ?? ""}`);
  const inFlight = useRef<AbortController | null>(null);
  // The part's top: a new category scrolls back to it, never to the page's top -- the profile sits above.
  const top = useRef<HTMLDivElement | null>(null);
  /** The cursor the last request asked from: what «Reintentar» asks again, never another list's. */
  const lastCursor = useRef<string | null>(null);
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setQ(text), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [text]);

  /** One page. A newer request aborts the older one, so a stale search never overwrites a fresh one. */
  const fetchPage = useCallback(
    async (cursor: string | null, search: string, category: string | null) => {
      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;
      lastCursor.current = cursor;
      // A first page is a new list: nothing of the previous one stays on screen
      // under the new title, nor feeds its cursor to a retry.
      if (cursor === null) {
        setItems([]);
        setNextCursor(null);
        setTotal(null);
        setPageOf({});
      }
      setStatus("loading");
      try {
        const response = await fetch(shopItemsUrl(slug, cursor, search, category, kind), { signal: controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const page = (await response.json()) as Page<PublicShopItem>;
        const listedBy = shopPageQuery(cursor, search, category, kind);
        setItems((before) => (cursor === null ? page.items : [...before, ...page.items]));
        setPageOf((before) => ({
          ...before,
          ...Object.fromEntries(page.items.map((item) => [item.token, listedBy])),
        }));
        setNextCursor(page.nextCursor);
        setTotal(page.total);
        setStatus("idle");
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("[shop] page failed", error);
        setStatus("failed");
      }
    },
    [slug, kind],
  );

  // The first page, and again whenever the search settles or the category changes.
  useEffect(() => {
    if (restoredFilter.current !== null) {
      if (restoredFilter.current === `${q}\u0000${section ?? ""}`) return;
      restoredFilter.current = null;
    }
    void fetchPage(null, q, section);
  }, [q, section, fetchPage]);

  // The next page when the bottom comes into view.
  useEffect(() => {
    const node = sentinel.current;
    if (node === null || nextCursor === null || status !== "idle") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void fetchPage(nextCursor, q, section);
      },
      { rootMargin: "600px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [nextCursor, status, q, section, fetchPage]);

  useEffect(() => {
    latest.current = { q, section, items, pageOf, total, nextCursor };
  }, [latest, q, section, items, pageOf, total, nextCursor]);

  const choose = (next: string | null): void => {
    if (next === section) return;
    setSection(next);
    // Only when the reader had scrolled past the part's top; otherwise the page stays where it is.
    const node = top.current;
    if (node !== null && node.getBoundingClientRect().top < 0) node.scrollIntoView({ block: "start" });
  };

  // Headings by section only while every category is shown: one category is its own title.
  const rows = withSectionHeaders(items, section === null ? sections : []);
  const searching = q.trim() !== "";
  const sectionName = sections.find((candidate) => candidate.id === section)?.name ?? null;
  // Beside the services the part names itself: a «Productos» heading over «Todo» said it twice.
  const title = searching ? `Resultados para "${q.trim()}"` : (sectionName ?? (shopWord ? "Todo" : "Productos"));
  const hasSections = sections.length > 0;

  return (
    <div ref={top} className="shop-layout">
      <aside className="shop-side">
        <input
          className="shop-search"
          type="search"
          inputMode="search"
          placeholder="Buscar"
          aria-label={shopWord ? "Buscar en la tienda" : "Buscar productos"}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />

        {hasSections ? (
          <>
            <nav className="shop-categories" aria-label="Categorías">
              <CategoryLink label="Todo" current={section === null} onChoose={() => choose(null)} />
              {sections.map((candidate) => (
                <CategoryLink
                  key={candidate.id}
                  label={candidate.name}
                  current={section === candidate.id}
                  onChoose={() => choose(candidate.id)}
                />
              ))}
            </nav>

            <select
              className="shop-category-select"
              aria-label="Categoría"
              value={section ?? ""}
              onChange={(event) => choose(event.target.value === "" ? null : event.target.value)}
            >
              <option value="">Todo</option>
              {sections.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </option>
              ))}
            </select>
          </>
        ) : null}
      </aside>

      <main className="shop-main">
        <div className="shop-heading">
          <h2>{title}</h2>
          {total === null ? null : (
            <span className="shop-count tabular">
              {total === 1 ? "1 resultado" : `${String(total)} resultados`}
            </span>
          )}
        </div>

        {items.length === 0 && status === "idle" ? (
          <p className="shop-empty">
            {searching ? `Sin resultados para "${q.trim()}"` : "Todavía no hay nada publicado"}
          </p>
        ) : (
          <div className="shop-grid">
            {rows.map((row) =>
              row.kind === "section" ? (
                <h3 key={row.key} className="shop-section">
                  {row.name}
                </h3>
              ) : (
                <ProductCard
                  key={row.key}
                  item={row.item}
                  cover={coverSrc(slug, row.item, pageOf[row.item.token] ?? "")}
                  country={shop.business.country}
                  onOpen={onOpen}
                />
              ),
            )}
            {status === "loading" && items.length === 0
              ? Array.from({ length: 6 }, (_, index) => (
                  <div key={`s-${String(index)}`} className="shop-card shop-card--skeleton share-skeleton" />
                ))
              : null}
          </div>
        )}

        {status === "failed" ? (
          <p className="shop-empty">
            No pudimos cargar esto.{" "}
            <button
              type="button"
              className="share-retry"
              onClick={() => void fetchPage(lastCursor.current, q, section)}
            >
              Reintentar
            </button>
          </p>
        ) : null}
        <div ref={sentinel} aria-hidden="true" />
      </main>
    </div>
  );
}

function CategoryLink({ label, current, onChoose }: { label: string; current: boolean; onChoose: () => void }) {
  return (
    <button
      type="button"
      className="shop-category"
      aria-current={current ? "true" : undefined}
      onClick={onChoose}
    >
      {label}
    </button>
  );
}

/** One product's card: in the grid, and under «Más productos» on an item's page. `onOpen` saves where the visitor was. */
export function ProductCard({
  item,
  cover,
  country,
  onOpen,
}: {
  item: PublicShopItem;
  cover: string | null;
  country: PublicShop["business"]["country"];
  onOpen?: () => void;
}) {
  const price = cardPrice(item, country);
  return (
    <a className="shop-card" href={`/p/${item.token}`} onClick={onOpen}>
      {cover === null ? (
        <span className="shop-card-photo shop-card-photo--initial" aria-hidden="true">
          {item.name.trim().charAt(0).toUpperCase()}
        </span>
      ) : (
        <img
          className="shop-card-photo"
          src={cover}
          alt=""
          loading="lazy"
          decoding="async"
        />
      )}
      <span className="shop-card-name">{item.name}</span>
      {price === null ? null : <span className="shop-card-price tabular">{price}</span>}
    </a>
  );
}
